/**
 * slideTransformSource.ts — turn the open presentation into a transform request.
 *
 * Reads the deck bytes from Office ONCE and derives everything the endpoint
 * needs from that single in-memory package: each selected slide's whole XML
 * document, the deck's slide size, and the theme palette.
 */

import { getActivePresentationBuffer } from "../../../utils/officePptx";
import {
  DeckStructureError,
  listSlidePartPaths,
  readPartText,
  readSlideDimensions,
  readSlideTheme,
  unzipDeck,
  type PackageFiles,
} from "./pptxDeckParts";
import { newRequestId } from "../../../utils/requestId";
import type { SelectedSlide } from "./slideSelection";
import type { SlideTransformInput, SlideTransformRequest } from "./slideTransformApi";

const REQUEST_ID_PREFIX = "slide-transform-";

export interface TransformSource {
  /** The unzipped deck, reused when writing results back. */
  readonly files: PackageFiles;
  /** Slide part path per selected slide, keyed by 0-based presentation position. */
  readonly partPathByIndex: ReadonlyMap<number, string>;
  readonly request: SlideTransformRequest;
}

/**
 * Build the transform request for `selectedSlides`.
 *
 * A deck can carry several masters and therefore several themes, but the
 * endpoint takes a single theme for the whole request — the first selected
 * slide's theme is used, which is the deck's own theme in the overwhelmingly
 * common single-master case.
 *
 * @throws {DeckStructureError} when the package is missing a required part or
 * none of the selected slides could be read.
 */
export async function buildTransformSource(
  selectedSlides: readonly SelectedSlide[],
): Promise<TransformSource> {
  if (selectedSlides.length === 0) {
    throw new DeckStructureError("No slides were selected to redesign.");
  }

  const deckBytes = await getActivePresentationBuffer();
  const files = unzipDeck(deckBytes);
  const slidePartPaths = listSlidePartPaths(files);
  const slideDimensions = readSlideDimensions(files);

  const partPathByIndex = new Map<number, string>();
  const slides: SlideTransformInput[] = [];

  for (const selected of selectedSlides) {
    const partPath = slidePartPaths[selected.index];
    // A selection that no longer maps onto the package (deck edited mid-run)
    // is skipped rather than failing the whole request.
    if (!partPath) continue;

    const slideXml = readPartText(files, partPath);
    if (!slideXml) continue;

    partPathByIndex.set(selected.index, partPath);
    slides.push({
      slide_index: selected.index,
      _xml: slideXml,
      slide_dimensions: slideDimensions,
    });
  }

  if (slides.length === 0) {
    throw new DeckStructureError("The selected slides could not be read from this presentation.");
  }

  const firstPartPath = partPathByIndex.get(slides[0]!.slide_index)!;
  const theme = readSlideTheme(files, firstPartPath);

  return {
    files,
    partPathByIndex,
    request: {
      request_id: newRequestId(REQUEST_ID_PREFIX),
      slides,
      theme: {
        theme_colors: theme.colors,
        ...(theme.fontFamily ? { font_family: theme.fontFamily } : {}),
      },
    },
  };
}
